import { Flex, Divider, Text, Box } from '@chakra-ui/core'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { memo, useCallback } from 'react'
import { CustomCheckBox } from '../GroupsTree/style'
import React from 'react'
import { FlatNode } from './hooks/useGroupsTree'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { RootState } from 'constants/interfaces'
import TagsPrevew from '../TagsPrevew'
import { SelectionMode } from './hooks/useSelection'
import { MAX_TAGS_IN_GROUP } from 'components/AdminConsole/Tags/utils'

interface TreeNodeProps {
  node: FlatNode
  isOpen: boolean
  isSelected: boolean
  toggleSelection: (oid: string) => void
  toggleNode: (oid: string) => void
  style: React.CSSProperties
  isIndeterminate: boolean
  isHighlighted?: boolean
  isShowTags?: boolean
  selectionMode: SelectionMode
}

export const TreeNode = memo(
  ({
    node,
    isOpen,
    toggleNode,
    style,
    isSelected,
    toggleSelection,
    isIndeterminate,
    isHighlighted,
    selectionMode,
    isShowTags = false,
  }: TreeNodeProps) => {
    const isRtl = useSelector(isRtlSelector)
    const theme = useSelector((state: RootState) => state.general.theme)
    const { tags } = useSelector((state: RootState) => state.config.config)

    let groupTags: typeof tags = []
    let isDisabled = false

    if (selectionMode === SelectionMode.RAW && isShowTags) {
      groupTags = tags.filter((tag) => tag.groups?.includes(node.oid))
      isDisabled = groupTags.length > MAX_TAGS_IN_GROUP
    }

    const handleToggleNode = useCallback(() => {
      if (!node.hasChildren) {
        return
      }
      toggleNode(node.oid)
    }, [node.hasChildren, node.oid, toggleNode])

    return (
      <Box
        style={style}
        pl={isRtl ? '15px' : `${node.level * 20 + 15}px`}
        pr={isRtl ? `${node.level * 20 + 15}px` : '15px'}
      >
        <Flex alignItems="center" height="60px" justifyContent="space-between">
          <Flex
            onClick={handleToggleNode}
            cursor="pointer"
            position="relative"
            alignItems="center"
            style={{ columnGap: '10px' }}
          >
            {node.hasChildren && (
              <Box>
                {isOpen ? (
                  <ChevronDown size={16} strokeWidth={3} />
                ) : isRtl ? (
                  <ChevronLeft size={16} strokeWidth={3} />
                ) : (
                  <ChevronRight size={16} strokeWidth={3} />
                )}
              </Box>
            )}

            <Flex
              flexDir="column"
              alignItems="flex-start"
              justifyContent="center"
              px={!node.hasChildren ? '20px' : '0px'}
            >
              <Flex alignItems="baseline" style={{ gap: 6 }}>
                <Text
                  isTruncated
                  flex="1"
                  fontSize="17px"
                  fontWeight={isHighlighted ? 700 : 600}
                  color="#202426"
                  w="max-content"
                  maxWidth="250px"
                >
                  {node.name}
                </Text>
                {node.descendantCount > 0 && (
                  <Text as="span" fontSize="12px" color="#87919a" flexShrink={0}>
                    <bdi dir="ltr">({node.descendantCount})</bdi>
                  </Text>
                )}
              </Flex>
              {isShowTags && groupTags && <TagsPrevew tags={groupTags} visibleCount={3} />}
            </Flex>
          </Flex>
          <CustomCheckBox
            color={theme?.elementsColor}
            isChecked={isSelected}
            isIndeterminate={isIndeterminate}
            onChange={() => toggleSelection(node.oid)}
            id={`checkbox-${node.oid}`}
            name={`checkbox-${node.name}`}
            isDisabled={isDisabled}
          />
        </Flex>
        <Divider my={0} />
      </Box>
    )
  }
)
